export interface GeometryQuestion {
  id: number;
  type: 'multiple-choice';
  points: number;
  question: string;
  options: string[];
  correctAnswer: number;
  solution: string[];
  topic: string;
  standard: string;
}

export const geometryTestNY: GeometryQuestion[] = [
  {
    id: 1, type: 'multiple-choice', points: 2,
    topic: 'Transformations', standard: 'G.CO.A.2',
    question: 'Which transformation maps triangle ABC onto triangle A\'B\'C\' such that every point moves the same distance in the same direction?',
    options: ['Rotation', 'Reflection', 'Translation', 'Dilation'],
    correctAnswer: 2,
    solution: [
      'A translation slides every point the same distance in the same direction.',
      'A rotation turns a figure around a fixed point.',
      'A reflection flips a figure over a line.',
      'A dilation changes the size of a figure.',
      'Answer: Translation'
    ]
  },
  {
    id: 2, type: 'multiple-choice', points: 2,
    topic: 'Triangle Congruence', standard: 'G.CO.B.8',
    question: 'Two triangles have two pairs of congruent sides and the included angles are congruent. Which congruence postulate applies?',
    options: ['SSS', 'SAS', 'ASA', 'AAS'],
    correctAnswer: 1,
    solution: [
      'SAS (Side-Angle-Side) requires two pairs of congruent sides AND the included angle between them.',
      'The included angle is the angle formed between the two known sides.',
      'SSS requires all three sides; ASA requires two angles and the included side; AAS requires two angles and a non-included side.',
      'Answer: SAS'
    ]
  },
  {
    id: 3, type: 'multiple-choice', points: 2,
    topic: 'Parallel Lines', standard: 'G.CO.C.9',
    question: 'Two parallel lines are cut by a transversal. If one interior angle measures 65°, what is the measure of its co-interior (same-side interior) angle?',
    options: ['65°', '115°', '25°', '90°'],
    correctAnswer: 1,
    solution: [
      'Co-interior angles (also called consecutive interior or same-side interior angles) are supplementary.',
      'Supplementary means they add up to 180°.',
      '180° − 65° = 115°',
      'Answer: 115°'
    ]
  },
  {
    id: 4, type: 'multiple-choice', points: 2,
    topic: 'Pythagorean Theorem', standard: 'G.SRT.B.4',
    question: 'A right triangle has legs of length 8 and 15. What is the length of the hypotenuse?',
    options: ['17', '23', '√161', '13'],
    correctAnswer: 0,
    solution: [
      'Use the Pythagorean Theorem: a² + b² = c²',
      '8² + 15² = c²',
      '64 + 225 = c²',
      '289 = c²',
      'c = √289 = 17',
      'Answer: 17'
    ]
  },
  {
    id: 5, type: 'multiple-choice', points: 2,
    topic: 'Trigonometry', standard: 'G.SRT.C.6',
    question: 'In right triangle ABC with the right angle at C, if angle A = 30° and the hypotenuse AB = 20, what is the length of side BC (opposite to angle A)?',
    options: ['10', '10√3', '20√3', '17.3'],
    correctAnswer: 0,
    solution: [
      'sin(A) = opposite / hypotenuse',
      'sin(30°) = BC / 20',
      '0.5 = BC / 20',
      'BC = 20 × 0.5 = 10',
      'Answer: 10'
    ]
  },
  {
    id: 6, type: 'multiple-choice', points: 2,
    topic: 'Circle Theorems', standard: 'G.C.A.2',
    question: 'An inscribed angle in a circle intercepts an arc of 140°. What is the measure of the inscribed angle?',
    options: ['140°', '280°', '70°', '40°'],
    correctAnswer: 2,
    solution: [
      'The Inscribed Angle Theorem states: an inscribed angle = half the intercepted arc.',
      'Inscribed angle = 140° ÷ 2 = 70°',
      'Answer: 70°'
    ]
  },
  {
    id: 7, type: 'multiple-choice', points: 2,
    topic: 'Area', standard: 'G.GMD.A.1',
    question: 'What is the area of a circle with radius 7?',
    options: ['14π', '49π', '7π', '28π'],
    correctAnswer: 1,
    solution: [
      'Area of a circle = πr²',
      'A = π(7)² = 49π',
      'Answer: 49π'
    ]
  },
  {
    id: 8, type: 'multiple-choice', points: 2,
    topic: 'Coordinate Geometry', standard: 'G.GPE.B.7',
    question: 'What is the distance between points (1, 2) and (4, 6)?',
    options: ['5', '7', '√7', '25'],
    correctAnswer: 0,
    solution: [
      'Distance formula: d = √[(x₂−x₁)² + (y₂−y₁)²]',
      'd = √[(4−1)² + (6−2)²]',
      'd = √[9 + 16]',
      'd = √25 = 5',
      'Answer: 5'
    ]
  },
  {
    id: 9, type: 'multiple-choice', points: 2,
    topic: 'Volume', standard: 'G.GMD.A.3',
    question: 'What is the volume of a cylinder with radius 3 and height 10?',
    options: ['30π', '90π', '60π', '9π'],
    correctAnswer: 1,
    solution: [
      'Volume of a cylinder = πr²h',
      'V = π(3)²(10)',
      'V = π(9)(10)',
      'V = 90π',
      'Answer: 90π'
    ]
  },
  {
    id: 10, type: 'multiple-choice', points: 2,
    topic: 'Similarity', standard: 'G.SRT.A.2',
    question: 'Two triangles are similar with a scale factor of 3:5. If the perimeter of the smaller triangle is 24, what is the perimeter of the larger triangle?',
    options: ['40', '72', '15', '36'],
    correctAnswer: 0,
    solution: [
      'Perimeters of similar figures are proportional to the scale factor.',
      'Scale factor = 3:5, so perimeters are in ratio 3:5.',
      '3/5 = 24/P',
      'P = 24 × 5/3 = 40',
      'Answer: 40'
    ]
  },
  {
    id: 11, type: 'multiple-choice', points: 2,
    topic: 'Angle Relationships', standard: 'G.CO.C.9',
    question: 'Two angles are vertical angles. One measures (3x + 10)° and the other measures (5x − 20)°. What is x?',
    options: ['x = 15', 'x = 10', 'x = 20', 'x = 5'],
    correctAnswer: 0,
    solution: [
      'Vertical angles are congruent (equal).',
      'Set the expressions equal: 3x + 10 = 5x − 20',
      'Subtract 3x: 10 = 2x − 20',
      'Add 20: 30 = 2x',
      'x = 15',
      'Answer: x = 15'
    ]
  },
  {
    id: 12, type: 'multiple-choice', points: 2,
    topic: 'Equation of a Circle', standard: 'G.GPE.A.1',
    question: 'What is the equation of a circle with center (2, −3) and radius 5?',
    options: [
      '(x−2)² + (y+3)² = 25',
      '(x+2)² + (y−3)² = 25',
      '(x−2)² + (y−3)² = 5',
      '(x+2)² + (y+3)² = 25'
    ],
    correctAnswer: 0,
    solution: [
      'Standard form of a circle: (x−h)² + (y−k)² = r²',
      'Center (h, k) = (2, −3), radius r = 5',
      '(x−2)² + (y−(−3))² = 5²',
      '(x−2)² + (y+3)² = 25',
      'Answer: (x−2)² + (y+3)² = 25'
    ]
  },
  {
    id: 13, type: 'multiple-choice', points: 2,
    topic: 'Dilation', standard: 'G.SRT.A.1',
    question: 'Point A(4, 6) is dilated by a scale factor of 1/2 centered at the origin. What are the coordinates of A\'?',
    options: ['(8, 12)', '(2, 3)', '(4, 3)', '(2, 6)'],
    correctAnswer: 1,
    solution: [
      'For a dilation centered at the origin with scale factor k: (x, y) → (kx, ky)',
      'k = 1/2, A = (4, 6)',
      'A\' = (1/2 × 4, 1/2 × 6) = (2, 3)',
      'Answer: (2, 3)'
    ]
  },
  {
    id: 14, type: 'multiple-choice', points: 2,
    topic: 'Triangle Similarity', standard: 'G.SRT.A.3',
    question: 'In two triangles, two pairs of corresponding angles are congruent. Which similarity criterion applies?',
    options: ['SSS~', 'SAS~', 'AA~', 'HL~'],
    correctAnswer: 2,
    solution: [
      'AA (Angle-Angle) Similarity: if two angles of one triangle are congruent to two angles of another, the triangles are similar.',
      'Since the sum of angles in a triangle is 180°, knowing two angles determines the third.',
      'Answer: AA~'
    ]
  },
  {
    id: 15, type: 'multiple-choice', points: 2,
    topic: 'Parallelogram Properties', standard: 'G.CO.C.11',
    question: 'In parallelogram ABCD, angle A = 70°. What is the measure of angle B?',
    options: ['70°', '110°', '140°', '35°'],
    correctAnswer: 1,
    solution: [
      'In a parallelogram, consecutive angles are supplementary (add to 180°).',
      'Angle B = 180° − 70° = 110°',
      'Answer: 110°'
    ]
  },
  {
    id: 16, type: 'multiple-choice', points: 2,
    topic: 'Surface Area', standard: 'G.GMD.A.3',
    question: 'What is the lateral surface area of a right circular cone with radius 4 and slant height 9?',
    options: ['36π', '16π', '13π', '72π'],
    correctAnswer: 0,
    solution: [
      'Lateral surface area of a cone = πrl',
      'where r = radius and l = slant height',
      'LSA = π(4)(9) = 36π',
      'Answer: 36π'
    ]
  },
  {
    id: 17, type: 'multiple-choice', points: 2,
    topic: 'Midpoint', standard: 'G.GPE.B.6',
    question: 'What is the midpoint of the segment with endpoints (−2, 4) and (6, −8)?',
    options: ['(2, −2)', '(4, −4)', '(2, 2)', '(−4, 12)'],
    correctAnswer: 0,
    solution: [
      'Midpoint formula: M = ((x₁+x₂)/2, (y₁+y₂)/2)',
      'M = ((−2+6)/2, (4+(−8))/2)',
      'M = (4/2, −4/2)',
      'M = (2, −2)',
      'Answer: (2, −2)'
    ]
  },
  {
    id: 18, type: 'multiple-choice', points: 2,
    topic: 'Exterior Angle Theorem', standard: 'G.CO.C.10',
    question: 'An exterior angle of a triangle measures 120°. The two non-adjacent interior angles are equal. What is each interior angle?',
    options: ['60°', '30°', '40°', '80°'],
    correctAnswer: 0,
    solution: [
      'Exterior Angle Theorem: an exterior angle equals the sum of the two non-adjacent interior angles.',
      '120° = x + x = 2x',
      'x = 60°',
      'Answer: 60°'
    ]
  },
  {
    id: 19, type: 'multiple-choice', points: 2,
    topic: 'Slope of Parallel Lines', standard: 'G.GPE.B.5',
    question: 'Line m has slope 3/4. What is the slope of a line parallel to m?',
    options: ['−4/3', '4/3', '3/4', '−3/4'],
    correctAnswer: 2,
    solution: [
      'Parallel lines have equal slopes.',
      'If line m has slope 3/4, any line parallel to m also has slope 3/4.',
      'Answer: 3/4'
    ]
  },
  {
    id: 20, type: 'multiple-choice', points: 2,
    topic: 'Arc Length', standard: 'G.C.B.5',
    question: 'A circle has radius 6. What is the arc length of a 60° central angle?',
    options: ['2π', '6π', '12π', 'π'],
    correctAnswer: 0,
    solution: [
      'Arc length = (central angle / 360°) × 2πr',
      'Arc length = (60/360) × 2π(6)',
      'Arc length = (1/6) × 12π',
      'Arc length = 2π',
      'Answer: 2π'
    ]
  },
  {
    id: 21, type: 'multiple-choice', points: 2,
    topic: 'Special Right Triangles', standard: 'G.SRT.C.8',
    question: 'In a 45-45-90 triangle, if each leg has length 5, what is the hypotenuse?',
    options: ['5√2', '10', '5√3', '5'],
    correctAnswer: 0,
    solution: [
      'In a 45-45-90 triangle, the hypotenuse = leg × √2',
      'Hypotenuse = 5 × √2 = 5√2',
      'Answer: 5√2'
    ]
  },
  {
    id: 22, type: 'multiple-choice', points: 2,
    topic: 'Polygon Angle Sum', standard: 'G.CO.C.10',
    question: 'What is the sum of the interior angles of a hexagon?',
    options: ['540°', '720°', '900°', '360°'],
    correctAnswer: 1,
    solution: [
      'Sum of interior angles of a polygon = (n − 2) × 180°',
      'For a hexagon, n = 6',
      'Sum = (6 − 2) × 180° = 4 × 180° = 720°',
      'Answer: 720°'
    ]
  },
  {
    id: 23, type: 'multiple-choice', points: 2,
    topic: 'Reflection', standard: 'G.CO.A.4',
    question: 'Point P(3, −5) is reflected over the x-axis. What are the coordinates of P\'?',
    options: ['(−3, −5)', '(3, 5)', '(−3, 5)', '(5, −3)'],
    correctAnswer: 1,
    solution: [
      'Reflection over the x-axis: (x, y) → (x, −y)',
      'P(3, −5) → P\'(3, −(−5)) = P\'(3, 5)',
      'Answer: (3, 5)'
    ]
  },
  {
    id: 24, type: 'multiple-choice', points: 2,
    topic: 'Volume of Pyramid', standard: 'G.GMD.A.3',
    question: 'A square pyramid has a base side of 6 and height of 9. What is its volume?',
    options: ['162', '54', '108', '324'],
    correctAnswer: 2,
    solution: [
      'Volume of a pyramid = (1/3) × Base Area × Height',
      'Base area = 6² = 36',
      'V = (1/3) × 36 × 9 = (1/3) × 324 = 108',
      'Answer: 108'
    ]
  },
  {
    id: 25, type: 'multiple-choice', points: 2,
    topic: 'Tangent Lines', standard: 'G.C.A.2',
    question: 'A tangent line to a circle is drawn from an external point. The tangent meets the radius at the point of tangency. What angle do they form?',
    options: ['45°', '60°', '90°', '180°'],
    correctAnswer: 2,
    solution: [
      'A tangent to a circle is perpendicular to the radius drawn to the point of tangency.',
      'Perpendicular means they form a 90° angle.',
      'Answer: 90°'
    ]
  },
  {
    id: 26, type: 'multiple-choice', points: 2,
    topic: 'Coordinate Proof', standard: 'G.GPE.B.4',
    question: 'A quadrilateral has vertices A(0,0), B(4,0), C(4,3), D(0,3). What type of quadrilateral is it?',
    options: ['Rhombus', 'Trapezoid', 'Rectangle', 'Parallelogram only'],
    correctAnswer: 2,
    solution: [
      'AB is horizontal (length 4), DC is horizontal (length 4) — opposite sides equal and parallel.',
      'AD is vertical (length 3), BC is vertical (length 3) — opposite sides equal and parallel.',
      'All angles are 90° (horizontal meets vertical).',
      'This is a rectangle.',
      'Answer: Rectangle'
    ]
  },
  {
    id: 27, type: 'multiple-choice', points: 2,
    topic: 'Isosceles Triangle', standard: 'G.CO.C.10',
    question: 'In isosceles triangle ABC, AB = AC. If angle A = 40°, what is angle B?',
    options: ['40°', '70°', '80°', '100°'],
    correctAnswer: 1,
    solution: [
      'In an isosceles triangle, the base angles are equal: angle B = angle C.',
      'Sum of angles = 180°: 40° + angle B + angle C = 180°',
      '40° + 2(angle B) = 180°',
      '2(angle B) = 140°',
      'angle B = 70°',
      'Answer: 70°'
    ]
  },
  {
    id: 28, type: 'multiple-choice', points: 2,
    topic: 'Perpendicular Slope', standard: 'G.GPE.B.5',
    question: 'A line has slope −2. What is the slope of a line perpendicular to it?',
    options: ['2', '−1/2', '1/2', '−2'],
    correctAnswer: 2,
    solution: [
      'Perpendicular lines have slopes that are negative reciprocals of each other.',
      'If slope = −2, the perpendicular slope = −1/(−2) = 1/2',
      'Answer: 1/2'
    ]
  },
  {
    id: 29, type: 'multiple-choice', points: 2,
    topic: 'Sector Area', standard: 'G.C.B.5',
    question: 'What is the area of a sector with radius 10 and central angle 90°?',
    options: ['25π', '100π', '10π', '50π'],
    correctAnswer: 0,
    solution: [
      'Sector area = (central angle / 360°) × πr²',
      'Sector area = (90/360) × π(10)²',
      'Sector area = (1/4) × 100π',
      'Sector area = 25π',
      'Answer: 25π'
    ]
  },
  {
    id: 30, type: 'multiple-choice', points: 2,
    topic: 'Cross-Sections', standard: 'G.GMD.B.4',
    question: 'A plane cuts a cone parallel to its base. What shape is the cross-section?',
    options: ['Triangle', 'Ellipse', 'Circle', 'Rectangle'],
    correctAnswer: 2,
    solution: [
      'When a plane cuts a cone parallel to its circular base, the cross-section is a circle.',
      'The circle will be smaller than the base if the cut is above the base.',
      'Answer: Circle'
    ]
  }
];
